import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { from, interval, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import * as _ from 'lodash-es';

import { Token } from './model/token.interface';
import { TokenCacheService } from './token-cache.service';
import { isNotEmpty, ValidateParameters } from '../../parameter-validators';
import { LocalStorageService } from '../../storage/local-storage.service';
import { HashService } from '../../hash.service';
import { Router } from '@angular/router';
import { DateHelper } from './date-helper';


/**
 * Handles account / authentication tokens.
 *
 * There are two types of token.
 * 1) account-specific tokens which holds all authorities to the given accounts and
 * 2) general session token which is not account-specific (aka the baseToken).
 *
 * This service uses a specific cache service to store the current (base / account related) tokens and in addition
 * it stores the current baseToken in the local storage for multi-tab functionality.
 *
 */
@Injectable({ providedIn: 'root' })
export class TokenService {

  private readonly authUrl = '/cloud-service-auth/auth';
  private readonly sessionTime = 1000 * 60 * 30; // 30 min by default
  private readonly extendSessionFactor = 0.8;

  private extendSessionTimerSubscription: Subscription;
  private baseSessionRenewalInProgress = false;

  constructor(private httpClient: HttpClient,
              private localStorageService: LocalStorageService,
              private tokenCacheService: TokenCacheService,
              private router: Router) {
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Public
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  registerBaseSessionToken(token: Token): void {

    if (_.isEmpty(token.value)) {
      return;
    }

    this.tokenCacheService.setToken(this.getBaseTokenHash(), token);
    this.localStorageService.setItem('baseToken', JSON.stringify(token));
    this.baseSessionRenewalInProgress = false;

    if (_.isEmpty(this.extendSessionTimerSubscription)) {
      this.startSessionRenewalTimer();
    }
  }

  getBaseToken$(): Observable<string> {
    return from(this.getBaseToken());
  }

  // TODO: Why use promise mixed with observables? I'd rather stick with one way or the other (prefere the rx way ;))
  getBaseToken(): Promise<string> {
    if (_.isEmpty(this.localStorageService.getItem('baseToken'))) {
      // UNAUTHORIZED
      // TODO: Why unauthorized?
      return Promise.reject('UNAUTHORIZED');

    } else {
      if (this.baseSessionRenewalInProgress) {
        return this.tokenCacheService.getTokenValue(this.getBaseTokenHash());
      } else {
        return this.getValidToken(this.getBaseTokenHash());
      }
    }
  }

  /*
   * Checks if the user is authenticated, i.e. a valid base session token is present.
   * Resolves to true if everything is fine, otherwise a rejection will occur.
   *
   * Note: If the session is currently extended, i.e. a new base token request was sent prior to function call,
   * then we cannot clearly make a statement about the authentication state and the promise of the request is returned.
   *
   */
  async isBaseTokenValid(): Promise<boolean> {
    try {
      await this.getBaseToken();
      return true;
    } catch (error) {
      this.unregisterAllTokens();
      return Promise.reject(error);
    }
  }

  @ValidateParameters
  getAccountToken$(@isNotEmpty accountIds: string[]): Observable<string> {
    return from(this.getAccountToken(accountIds));
  }

  @ValidateParameters
  async getAccountToken(@isNotEmpty accountIds: string[]): Promise<string> {
    try {
      if (await this.isBaseTokenValid()) {
        const hash = this.generateHash(accountIds);
        return this.getValidToken(hash, accountIds);
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  unregisterAllTokens() {
    this.localStorageService.removeItem('baseToken');
    this.tokenCacheService.clear();
    if (!_.isEmpty(this.extendSessionTimerSubscription)) {
      this.extendSessionTimerSubscription.unsubscribe();
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Private
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private getBaseTokenHash() {
    return this.generateHash([]);
  }

  private getValidToken(hash: number, accountIds?: string[]): Promise<string> {
    let tokenValue = this.tokenCacheService.getTokenValueIfNotExpiredUntil(hash, DateHelper.new());

    if (_.isEmpty(tokenValue)) {
      tokenValue
        = hash === this.getBaseTokenHash()
        ? this.requestAndCacheToken(hash)
        : this.requestAndCacheToken(hash, accountIds);
    }

    return tokenValue;
  }

  private generateHash(accountIds: string[]): number {
    if (_.isEmpty(accountIds)) {
      return 0;
    }

    const sortedAccountIds = accountIds.sort();
    const accountIdsAsString = sortedAccountIds.join('');

    return HashService.generate(accountIdsAsString);
  }

  private requestAndCacheToken(hash: number, accountIds?: string[]): Promise<string> {
    const baseTokenValue = JSON.parse(this.localStorageService.getItem('baseToken')).value;
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + baseTokenValue);
    const body = {
      accountIds: accountIds ? accountIds : [],
    };

    return this.httpClient.post<Token>(this.authUrl, body, { headers }).pipe(
      catchError(error => {
        this.router.navigate(['/']); // redirect to login
        return throwError(error);
      }),
      map((token: Token) => {
        this.tokenCacheService.setToken(hash, token);
        return token.value;
      }),
    ).toPromise();
  }

  private startSessionRenewalTimer() {
    const timer = interval(this.sessionTime * this.extendSessionFactor);
    this.extendSessionTimerSubscription = timer.subscribe(() => {
      this.extendSession();
    });
  }

  private extendSession(): Promise<string> {
    if (!_.isEmpty(this.localStorageService.getItem('baseToken'))) {

      this.baseSessionRenewalInProgress = true;

      const baseTokenValue = JSON.parse(this.localStorageService.getItem('baseToken')).value;
      const headers = new HttpHeaders().set('Authorization', 'Bearer ' + baseTokenValue);
      const body = {
        prolong: true,
        lifetime: this.sessionTime,
      };

      /*
       * When we extend the session and try to get a new base token we also want to register it again, i.e. we
       * store the new base token in the cache and the local storage.
       * In the meantime, we also update the current value of the base token entry in the cache because it is
       * necessary if the ``getBaseToken`` method is called during the request.
       */
      const baseTokenPromise = this.httpClient.post<Token>(this.authUrl, body, { headers })
        .pipe(map((token: Token) => {
          this.registerBaseSessionToken(token);
          return token.value;
        }))
        .toPromise();
      this.tokenCacheService.updateTokenValue(this.getBaseTokenHash(), baseTokenPromise);

      return baseTokenPromise;
    }
  }
}
